import React, { ReactNode, useEffect, useRef, useState } from 'react';

import styles from './FlyRightPane.module.less';

export interface FlyRightPaneProps {
  isOpen?: boolean;
  initialWidth?: number;
  minWidth?: number;
  maxWidth?: number;
  resizable?: boolean;
  onClose?: () => void;
  children?: ReactNode;
}

// Leave at least 520px for the main content area
const CONTENT_AREA_MIN_WIDTH = 520;
const calculateMaxWidth = (
  containerWidth: number | undefined,
  minWidth: number
) => {
  if (!containerWidth) return minWidth;
  return containerWidth - CONTENT_AREA_MIN_WIDTH < minWidth
    ? minWidth
    : containerWidth - CONTENT_AREA_MIN_WIDTH;
};

const rpKey: string =
  import.meta.env.VITE_SESSION_STORAGE_RIGHT_PANE_KEY || 'right-pane';

// Persistence is best-effort — sessionStorage can throw in Safari private
// mode, restrictive CSP, etc. UI must continue working when storage fails.
const readStoredWidth = (fallback: number): number => {
  try {
    const stored = sessionStorage.getItem(rpKey);
    return stored ? Number(stored) : fallback;
  } catch {
    return fallback;
  }
};

const writeStoredWidth = (value: number): void => {
  try {
    sessionStorage.setItem(rpKey, value.toString());
  } catch {
    // ignore — width is purely a UX preference
  }
};

export const FlyRightPane: React.FC<FlyRightPaneProps> = ({
  isOpen = true,
  initialWidth = 324,
  minWidth = 324,
  resizable = true,
  onClose,
  children,
  ...rest
}) => {
  const [width, setWidth] = useState<number>(() =>
    readStoredWidth(initialWidth)
  );

  // TODO: Add breakpoint constant
  const isMobile = window.innerWidth <= 768;
  const shouldResize = resizable && !isMobile;

  const widthRef = useRef<number>(width);
  const minWidthRef = useRef<number>(minWidth);

  useEffect(() => {
    const container = document.querySelector('.dashboard-container');

    const handleResize = () => {
      const width = widthRef.current;
      const minWidth = minWidthRef.current;

      const containerWidth = container?.clientWidth || window.innerWidth;

      const newMaxWidth = calculateMaxWidth(containerWidth, minWidth);
      writeStoredWidth(width);
      if (width > newMaxWidth) {
        setWidth(newMaxWidth);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const startResizing = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();

    const startX = e.clientX;
    const startWidth = width;

    const container = document.querySelector('.dashboard-container');

    const onMouseMove = (moveEvent: MouseEvent) => {
      const containerWidth = container?.clientWidth || 0;
      const maxWidth = calculateMaxWidth(containerWidth, minWidth);

      const deltaX = startX - moveEvent.clientX;
      const newWidth = startWidth + deltaX;
      const clampedWidth = Math.min(Math.max(newWidth, minWidth), maxWidth);
      if (clampedWidth < containerWidth) {
        setWidth(clampedWidth);
        widthRef.current = clampedWidth;
        writeStoredWidth(clampedWidth);
      }
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div
      {...rest}
      className={styles.rightPaneContainer}
      style={{
        width: `${width}px`,
        minWidth: `${minWidth}px`,
        maxWidth: `${width}px`,
      }}
    >
      {children}
      {shouldResize && (
        <div
          data-testid='draggable'
          onMouseDown={startResizing}
          className={styles.drawerBorder}
        />
      )}
    </div>
  );
};
